// Someone talking to Arc on the call is answered, whatever the model thinks. Only a
// direct address counts ("Arc, pull…", "hey Arc what…"): a call about Arc names it
// constantly ("send it to arc, say…", "ARC is able to…"), so "Arc," after a preposition
// and Arc as a sentence's subject don't.
const ARC_THEN_COMMA =
  /(?<!\b(?:to|for|with|through|in|on|of|from|about|at|by|into|like|the|our|using|use)\s)\barc\s*[,:]/i
const ARC_THEN_REQUEST =
  /(?:^|[.!?]\s+|\b(?:hey|hi|hello|ok|okay|yo|so)[,\s]+)arc\s+(?:can|could|would|will you|what|what's|who|when|where|how|pull|give|tell|show|find|look|check|please)\b/i
// The openers a line can start on before it speaks to Arc ("hey Arc", "so Arc").
const OPENERS = 'hey|hi|ok|okay|yo|so'
// Its name as a whole sentence: "…can you log that, please? Arc."
const ARC_ALONE = new RegExp(`^(?:(?:${OPENERS})[,\\s]+)?arc[.!?]*$`, 'i')
// Fireflies hears "Arc" as "Ark", and as "Mark" unless someone on the call is called Mark.
const MISHEARD_ARC = /\bark\b/gi
const MISHEARD_ARC_AS_MARK = /\bmark\b/gi
// Arc is in a huddle's room, so a line opening on its name is said to it ("Arc actually,
// what…"), unless it's about Arc ("Arc is…"); or one ending on it ("right, Arc?").
const ROOM_OPENS_ON_ARC = new RegExp(
  `^(?:(?:${OPENERS}|and|but|right)[,\\s]+)?arc\\b(?!['’]s)` +
    "(?!\\s+(?:is|was|has|had|did|does|seems|just|isn't|wasn't|doesn't|didn't)\\b)",
  'i',
)
const ROOM_ENDS_ON_ARC = /,\s*arc\s*[?.!]*$/i

const SENTENCE_END = /[.!?]["')\]]*\s*$/
const TRAILS_OFF = /(?:\.{2,}|…|[,:;\-–—])["')\]]*\s*$/
// A noun has to follow these, however long the ask before them. Not "her" or "his":
// "email her" ends a request as often as "her deck" continues one. A capital "A" is a
// label ("Exhibit A", "Series A"), not an article.
const NEEDS_A_NOUN = /\b(?:a|[Aa]n|[Tt]he|[Mm]y|[Oo]ur|[Tt]heir|[Yy]our|[Ii]ts)\W*$/
// Ends a request that hasn't started ("Arc, could you"), not one tagged on after the ask.
const ASKS_NEXT = /\b(?:can|could|would|will) you\W*$/i
// A preposition can end a question ("what are they looking for?"), just not unpunctuated talk.
const LEFT_HANGING = /\b(?:to|for|of|on|in|into|about|with|from|at|by|like)[\s,]*$/i
// What people tack onto a finished ask: "pull their financials, um", "…financials and".
const FILLER_WORD = /^(?:um|uh|and|or|but)[.!?]*$/i
const PAUSES = /[\s,;:\-–—]+/
// The wake word and a polite opener carry no ask: "hey Arc, could you please…".
const UP_TO_ARC = /^.*?\barc\b\W*/i
const OPENER = /^(?:(?:please|can|could|would|will|you)\b\W*)*/i
// Fewer words than this before a dangling end ("pull up the", "look into") names nothing.
const MIN_ASK_WORDS = 3
// An ellipsis doesn't end a sentence: "could you... pull their AR" is one thought.
const SENTENCE_BREAK = /(?<!\.\.)(?<=[.!?])\s+/

const hearArk = (line: string) => line.replace(MISHEARD_ARC, 'Arc')

/** A transcript line as speech: no "[0:05] Name:" prefix, and Arc as Fireflies mishears it. */
const toSpeech = (line: string, speakerNames: string[]) => {
  const isMarkOnCall = speakerNames.some((name) => /\bmark\b/i.test(name))
  const heard = hearArk(line.replace(/^\[[^\]]*\][^:]*:\s*/, ''))
  return isMarkOnCall ? heard : heard.replace(MISHEARD_ARC_AS_MARK, 'Arc')
}

/** Someone on the call spoke to Arc, in what they said rather than in a speaker's name. */
export const isAddressedToArc = (lines: string[], speakerNames: string[] = []): boolean =>
  lines.some((line) => {
    const speech = toSpeech(line, speakerNames)
    return (
      ARC_THEN_COMMA.test(speech) ||
      ARC_THEN_REQUEST.test(speech) ||
      speech.split(/(?<=[.!?])\s+/).some((sentence) => ARC_ALONE.test(sentence.trim()))
    )
  })

/** Spoken to Arc in a huddle: the strict forms above, plus the looser ones a room uses. */
export const isAddressedInRoom = (text: string): boolean => {
  const line = text.trim()
  const heard = hearArk(line)
  return isAddressedToArc([line]) || ROOM_OPENS_ON_ARC.test(heard) || ROOM_ENDS_ON_ARC.test(heard)
}

const withoutTrailingFiller = (sentence: string) => {
  const words = sentence.split(PAUSES).filter(Boolean)
  while (words.length && FILLER_WORD.test(words[words.length - 1])) {
    words.pop()
  }
  return words.join(' ')
}

const endsShort = (said: string) =>
  ARC_ALONE.test(said) || TRAILS_OFF.test(said) || ASKS_NEXT.test(said) || LEFT_HANGING.test(said)

/** Words of the ask itself: past the wake word and opener, short of a dangling last word. */
const askWordCount = (said: string) => {
  const ask = said.replace(UP_TO_ARC, '').replace(OPENER, '')
  const words = ask.split(/\s+/).filter(Boolean).length
  return ASKS_NEXT.test(ask) || LEFT_HANGING.test(ask) ? words - 1 : words
}

/** A sentence that stops before saying anything: "could you…", "pull the", "Arc.", "Um." */
const isUnfinished = (sentence: string) => {
  const said = withoutTrailingFiller(sentence)
  return !said || NEEDS_A_NOUN.test(said) || (endsShort(said) && askWordCount(said) < MIN_ASK_WORDS)
}

/**
 * They spoke to Arc but never said what they want: every sentence from the address on
 * stops short. A bare "Arc." takes the sentence before it as the ask ("Log that? Arc.").
 */
export const isCutOff = (text: string, speakerNames: string[] = []): boolean => {
  const sentences = toSpeech(text, speakerNames).trim().split(SENTENCE_BREAK)
  const addressAt = sentences.findIndex((sentence) => isAddressedToArc([sentence], speakerNames))
  const askedAt = ARC_ALONE.test(sentences[addressAt] ?? '') ? addressAt - 1 : addressAt
  return sentences.slice(Math.max(0, askedAt)).every(isUnfinished)
}

/** The line is only Arc's name, so the ask is in the line before it. */
export const isWakeWordOnly = (line: string, speakerNames: string[] = []): boolean =>
  ARC_ALONE.test(toSpeech(line, speakerNames).trim())

/** The line is still going: no sentence end yet, or it trails off. */
export const isMidSentence = (text: string): boolean =>
  !SENTENCE_END.test(text) || TRAILS_OFF.test(text)
